import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Icon } from '@kontentino/ui';
import { faWindowMinimize } from '@fortawesome/pro-regular-svg-icons/faWindowMinimize';
import { useChecklistTasks } from 'app/modules/onboardingChecklist/hooks/useChecklistTasks';
import {
  useChecklistAutoExpand,
  useChecklistVisibility,
} from 'app/modules/onboardingChecklist/hooks/useChecklistVisibility';
import { useOnboardingChecklistConfig } from 'app/modules/onboardingChecklist/hooks/useOnboardingChecklistConfig';
import { useChecklistCompletionTracking } from 'app/modules/onboardingChecklist/hooks/useChecklistCompletionTracking';
import { ChecklistTaskState } from 'app/modules/onboardingChecklist/types';
import ChecklistTaskItem from 'app/modules/onboardingChecklist/components/ChecklistTaskItem';

const TRANSITION_CLASSES =
  'tw-transition-all tw-duration-300 tw-ease-out tw-origin-bottom-right';

const OnboardingChecklist: React.FC = () => {
  const { t } = useTranslation();
  const { isTrial } = useOnboardingChecklistConfig();
  const taskHookResults = useChecklistTasks();
  const tasks: ChecklistTaskState[] = taskHookResults.map(
    (result) => result.task,
  );
  const modalElements = taskHookResults.map((result) => result.modalElement);

  const { isCollapsed, setCollapsed } = useChecklistVisibility();

  useChecklistAutoExpand();
  useChecklistCompletionTracking(tasks);

  const doneCount = useMemo(
    () => tasks.filter((task) => task.isDone).length,
    [tasks],
  );
  const totalCount = tasks.length;
  const isAllDone = doneCount === totalCount && totalCount > 0;
  const progressLabel = t('onboardingChecklist.progress', {
    current: doneCount,
    total: totalCount,
  });

  if (isTrial && isAllDone) {
    return null;
  }

  return (
    <>
      <div
        className="tw-fixed tw-bottom-20 tw-right-6 tw-z-40"
        data-cy="onboarding-checklist"
        data-name="onboarding-checklist"
      >
        <div
          aria-hidden={isCollapsed}
          className={clsx(
            'tw-absolute tw-bottom-0 tw-right-0 tw-w-[350px] tw-overflow-hidden tw-rounded-xl tw-border tw-border-grayscale-10 tw-bg-white tw-shadow-[0_24px_48px_-12px_rgba(0,0,0,0.24),0_8px_16px_-8px_rgba(33,70,236,0.18)]',
            TRANSITION_CLASSES,
            isCollapsed
              ? 'tw-pointer-events-none tw-scale-90 tw-opacity-0'
              : 'tw-scale-100 tw-opacity-100',
          )}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-rounded-t-xl tw-bg-gradient-to-b tw-from-primary-100 tw-to-primary-160 tw-p-4 tw-text-white">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <div className="tw-text-md tw-font-regular tw-text-white">
                {t('onboardingChecklist.title')}
              </div>
              <div className="tw-text-sm tw-text-white/80">{progressLabel}</div>
            </div>
            <span
              role="button"
              tabIndex={isCollapsed ? -1 : 0}
              onClick={() => setCollapsed(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setCollapsed(true);
                }
              }}
              aria-label={t('onboardingChecklist.minimize')}
              data-cy="onboarding-checklist_collapse-button"
              data-name="onboarding-checklist-collapse"
              className="tw-flex tw-cursor-pointer tw-items-center tw-text-white"
            >
              <Icon icon={faWindowMinimize} />
            </span>
          </div>
          <ul className="tw-flex tw-max-h-[400px] tw-flex-col tw-overflow-y-auto">
            {tasks.map((task) => (
              <ChecklistTaskItem key={task.id} task={task} />
            ))}
          </ul>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-hidden={!isCollapsed}
          tabIndex={isCollapsed ? 0 : -1}
          aria-label={
            isAllDone
              ? t('onboardingChecklist.allTasksCompleted')
              : progressLabel
          }
          className={clsx(
            'tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-whitespace-nowrap tw-rounded-full tw-bg-gradient-to-b tw-from-primary-100 tw-to-primary-160 tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-text-white tw-shadow-[0_12px_32px_-8px_rgba(33,70,236,0.55),0_4px_12px_rgba(0,0,0,0.12)]',
            TRANSITION_CLASSES,
            isCollapsed
              ? 'tw-scale-100 tw-opacity-100 hover:tw-scale-110'
              : 'tw-pointer-events-none tw-scale-50 tw-opacity-0',
          )}
          data-cy="onboarding-checklist_expand-button"
          data-name="onboarding-checklist-expand"
        >
          <span>{t('onboardingChecklist.collapsedTitle')}</span>
          <span>
            {doneCount}/{totalCount}
          </span>
        </button>
      </div>
      {modalElements}
    </>
  );
};

export default OnboardingChecklist;
